import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logo from '../../assets/logo.svg';
import { useAuth } from '../../contexts/AuthContext';
import { User, Lock, Mail, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';

export default function LoginForm({ onSwitchToSignup }) {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      setIsSubmitting(false);
      return;
    }

    try {
      // Direct API call to check for error handling
      const response = await axios.post('/api/auth/login', {
        email: formData.email,
        password: formData.password
      }).catch(error => {
        console.error('Login API error:', error.response?.data);
        if (error.response?.status === 401) {
          setError(error.response.data.detail || 'Incorrect email or password');
          return { success: false };
        }
        throw error;
      });

      if (response.success === false) {
        // Error was already handled in the catch block
        setIsSubmitting(false);
        return;
      }

      // If we get here, login was successful
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Handle role-specific redirect
        const redirectPath = result.redirectPath || '/dashboard';
        navigate(redirectPath, { replace: true });
      } else {
        // Enhanced error handling with fallback
        const errorMessage = result.error || 'Login failed. Please check your credentials and try again.';
        setError(errorMessage);
        console.log('Login failed:', errorMessage);
      }
    } catch (error) {
      console.error('Login exception:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#EDF0F1'}}>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src={logo} 
            alt="Fortress Asset Managers Logo" 
            className="mx-auto h-16 w-16"
          />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Access Fortress Asset Managers
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200" role="alert">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#00C5CB'}}
                    onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#00C5CB'}}
                    onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 text-right">
              <Link
                to="/forgot-password"
                className="text-sm font-medium hover:underline transition-colors"
                style={{color: '#00C5CB'}}
              >
                Forgot your password?
              </Link>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity"
                style={{backgroundColor: '#00C5CB', '--tw-ring-color': '#00C5CB'}}
                onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={onSwitchToSignup}
                className="text-sm font-medium hover:underline"
                style={{color: '#00C5CB'}}
                disabled={isSubmitting}
              >
                Don't have an account? Sign up
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}